import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import NotificationCard from './components/NotificationCard';
import FilterBar from './components/FilterBar';
import BulkActions from './components/BulkActions';
import {
  getUserNotifications,
  getUnreadCount,
  markAsRead,
  markMultipleAsRead,
  markAllAsRead,
  deleteNotification,
  deleteMultipleNotifications,
  subscribeToNotifications,
  subscribeToUnreadCount,
  unsubscribeChannel
} from '../../services/notificationsService';

const Notifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filters, setFilters] = useState({
    type: 'all',
    priority: 'all',
    isRead: 'all'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const channelRef = useRef(null);
  const unreadChannelRef = useRef(null);

  // Load notifications
  useEffect(() => {
    if (!user) return;

    const loadNotifications = async () => {
      try {
        setLoading(true);
        setError(null);
        const [notificationsData, unreadCountData] = await Promise.all([
          getUserNotifications(),
          getUnreadCount()
        ]);
        setNotifications(notificationsData || []);
        setUnreadCount(unreadCountData);
      } catch (err) {
        console.error('Error loading notifications:', err);
        setError('Failed to load notifications');
      } finally {
        setLoading(false);
      }
    };

    loadNotifications();

    // Subscribe to real-time updates
    if (user?.id) {
      channelRef.current = subscribeToNotifications(user?.id, (payload) => {
        if (payload?.type === 'INSERT') {
          setNotifications(prev => [
            { ...payload?.notification, isNew: true },
            ...prev
          ]);
          // Show browser notification for new alerts
          if ('Notification' in window && Notification?.permission === 'granted') {
            new Notification(payload?.notification?.title, {
              body: payload?.notification?.message,
              icon: '/assets/images/LOGO-1771655664486.png'
            });
          }
          // Remove isNew flag after 3 seconds
          setTimeout(() => {
            setNotifications(prev => 
              prev?.map(n => 
                n?.id === payload?.notification?.id 
                  ? { ...n, isNew: false } 
                  : n
              )
            );
          }, 3000);
        } else if (payload?.type === 'UPDATE') {
          setNotifications(prev => 
            prev?.map(n => n?.id === payload?.notification?.id ? payload?.notification : n)
          );
        } else if (payload?.type === 'DELETE') {
          setNotifications(prev => prev?.filter(n => n?.id !== payload?.notificationId));
        }
      });

      // Subscribe to unread count updates
      unreadChannelRef.current = subscribeToUnreadCount(user?.id, (count) => {
        setUnreadCount(count);
      });
    }

    return () => {
      if (channelRef?.current) {
        unsubscribeChannel(channelRef?.current);
      }
      if (unreadChannelRef?.current) {
        unsubscribeChannel(unreadChannelRef?.current);
      }
    };
  }, [user]);

  // Apply filters
  useEffect(() => {
    let filtered = [...notifications];

    if (filters?.type !== 'all') {
      filtered = filtered?.filter(n => n?.type === filters?.type);
    }

    if (filters?.priority !== 'all') {
      filtered = filtered?.filter(n => n?.priority === filters?.priority);
    }

    if (filters?.isRead === 'read') {
      filtered = filtered?.filter(n => n?.isRead === true);
    } else if (filters?.isRead === 'unread') {
      filtered = filtered?.filter(n => n?.isRead === false);
    }

    setFilteredNotifications(filtered);
  }, [notifications, filters]);

  // Handle filter change
  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setSelectedIds([]);
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    try {
      if (!notification?.isRead) {
        await markAsRead(notification?.id);
      }
      if (notification?.actionUrl) {
        navigate(notification?.actionUrl);
      }
    } catch (err) {
      console.error('Error handling notification click:', err);
    }
  };

  // Handle mark as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      await markAsRead(notificationId);
    } catch (err) {
      console.error('Error marking as read:', err);
      setError('Failed to mark notification as read');
    }
  };

  // Handle delete
  const handleDelete = async (notificationId) => {
    try {
      await deleteNotification(notificationId);
    } catch (err) {
      console.error('Error deleting notification:', err);
      setError('Failed to delete notification');
    }
  };

  // Handle select notification
  const handleSelectNotification = (notificationId) => {
    setSelectedIds(prev => {
      if (prev?.includes(notificationId)) {
        return prev?.filter(id => id !== notificationId);
      } else {
        return [...prev, notificationId];
      }
    });
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedIds?.length === filteredNotifications?.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredNotifications?.map(n => n?.id));
    }
  };

  // Handle bulk mark as read
  const handleBulkMarkAsRead = async () => {
    try {
      await markMultipleAsRead(selectedIds);
      setSelectedIds([]);
    } catch (err) {
      console.error('Error marking notifications as read:', err);
      setError('Failed to mark notifications as read');
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    try {
      await deleteMultipleNotifications(selectedIds);
      setSelectedIds([]);
    } catch (err) {
      console.error('Error deleting notifications:', err);
      setError('Failed to delete notifications');
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (err) {
      console.error('Error marking all as read:', err);
      setError('Failed to mark all notifications as read');
    }
  };

  // Group notifications by type
  const groupedNotifications = filteredNotifications?.reduce((acc, notification) => {
    const type = notification?.type;
    if (!acc?.[type]) {
      acc[type] = [];
    }
    acc?.[type]?.push(notification);
    return acc;
  }, {});

  const typeLabels = {
    job_application: 'Job Applications',
    message: 'Messages',
    payment: 'Payments',
    profile_activity: 'Profile Activity',
    system: 'System'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-[60px] lg:pt-[60px]">
        <Helmet>
          <title>Notifications - DISKARTE</title>
        </Helmet>
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="text-[#1E73BE] animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading notifications...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-[60px] lg:pt-[60px]">
      <Helmet>
        <title>Notifications - DISKARTE</title>
      </Helmet>
      <Header />
      <div className="container mx-auto px-4 py-8 lg:py-10 max-w-5xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl lg:text-4xl font-heading font-bold text-gray-900 flex items-center gap-3">
                <Icon name="Bell" size={32} className="text-[#FF6A00]" />
                Notifications
              </h1>
              <p className="text-base text-gray-600 mt-2">
                {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
              </p>
            </div>
            {unreadCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleMarkAllAsRead}
                iconName="CheckCheck"
                iconPosition="left"
              >
                Mark all as read
              </Button>
            )}
          </div>

          {/* Filter Bar */}
          <FilterBar filters={filters} onFilterChange={handleFilterChange} />
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <Icon name="AlertCircle" size={20} className="text-red-600" />
            <p className="text-sm text-red-600">{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-700"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        )}

        {/* Bulk Actions */}
        {selectedIds?.length > 0 && (
          <BulkActions
            selectedCount={selectedIds?.length}
            onMarkAsRead={handleBulkMarkAsRead}
            onDelete={handleBulkDelete}
            onCancel={() => setSelectedIds([])}
          />
        )}

        {/* Notifications List */}
        {filteredNotifications?.length > 0 ? (
          <div className="space-y-8">
            {Object.keys(groupedNotifications)?.map((type) => (
              <div key={type} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <Icon 
                      name={
                        type === 'job_application' ? 'Briefcase' :
                        type === 'message' ? 'MessageSquare' :
                        type === 'payment' ? 'DollarSign' :
                        type === 'profile_activity' ? 'User' : 'Bell'
                      } 
                      size={16} 
                      className="text-[#1E73BE]" 
                    />
                    {typeLabels?.[type] || type}
                  </h2>
                  <span className="text-xs text-gray-600 font-medium">
                    {groupedNotifications?.[type]?.length} notification{groupedNotifications?.[type]?.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="divide-y divide-gray-200">
                  {filteredNotifications?.map((notification) => (
                    <NotificationCard
                      key={notification?.id}
                      notification={notification}
                      isSelected={selectedIds?.includes(notification?.id)}
                      isNew={notification?.isNew}
                      onSelect={handleSelectNotification}
                      onClick={handleNotificationClick}
                      onMarkAsRead={handleMarkAsRead}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-16 text-center">
            <Icon name="Bell" size={64} className="text-gray-400 mx-auto mb-6 opacity-50" />
            <h3 className="text-lg font-semibold text-gray-900 mb-3">No notifications</h3>
            <p className="text-sm text-gray-600">
              {filters?.type !== 'all' || filters?.priority !== 'all' || filters?.isRead !== 'all' ?'No notifications match your filters' :'You have no notifications at the moment'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;